/**
 * eReg Dashboard Page
 *
 * Overview of pending signatures, missing documents, and protocol status.
 */

import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  PenTool,
  AlertTriangle,
  Clock,
  FileText,
  ChevronRight,
  FolderOpen,
} from 'lucide-react';
import { useEreg } from '../contexts/EregContext';
import { useAuth } from '../../contexts/AuthContext';
import { PageHeader, SearchInput } from '../../ui';
import * as eregApi from '../lib/api';
import type { DashboardStatsResponse, SearchResult } from '../types';

export function Dashboard() {
  const { protocols, protocolsLoading, setCurrentProtocol, setSiteMode } = useEreg();
  const { currentSite } = useAuth();
  const [stats, setStats] = useState<DashboardStatsResponse | null>(null);
  const [statsLoading, setStatsLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState<SearchResult[]>([]);
  const [isSearching, setIsSearching] = useState(false);

  // Fetch dashboard stats
  useEffect(() => {
    async function fetchStats() {
      setStatsLoading(true);
      try {
        const dashboardStats = await eregApi.getDashboardStats({
          site_id: currentSite?.id,
        });
        setStats(dashboardStats);
      } catch (error) {
        console.error('Failed to fetch dashboard stats:', error);
      } finally {
        setStatsLoading(false);
      }
    }

    if (currentSite?.id) {
      fetchStats();
    }
  }, [currentSite?.id]);

  const handleProtocolClick = (protocol: typeof protocols[0]) => {
    setCurrentProtocol(protocol);
    setSiteMode(false);
  };

  const handleSiteDocsClick = () => {
    setCurrentProtocol(null);
    setSiteMode(true);
  };

  // Handle search
  async function handleSearch() {
    if (searchQuery.length < 2) return;

    setIsSearching(true);
    try {
      const response = await eregApi.searchDocuments({
        query: searchQuery,
        site_id: currentSite?.id,
        limit: 10,
      });
      setSearchResults(response.results);
    } catch (error) {
      console.error('Search failed:', error);
    } finally {
      setIsSearching(false);
    }
  }

  // Debounce search
  useEffect(() => {
    if (searchQuery.length < 2) {
      setSearchResults([]);
      return;
    }

    const timer = setTimeout(() => {
      handleSearch();
    }, 300);

    return () => clearTimeout(timer);
  }, [searchQuery]);

  return (
    <div className="max-w-7xl mx-auto space-y-6">
      {/* Header with Search */}
      <PageHeader
        title="eReg Dashboard"
        subtitle="Electronic Regulatory Document Management"
        actions={
          <div className="relative w-full md:w-80">
            <SearchInput
              value={searchQuery}
              onChange={setSearchQuery}
              placeholder="Search documents..."
              label="Search documents"
            />

            {/* Search Results Dropdown */}
            {searchResults.length > 0 && (
              <div className="absolute z-10 mt-1 w-full rounded-lg border border-gray-200 bg-white shadow-lg dark:border-gray-700 dark:bg-gray-800 max-h-80 overflow-auto">
                {searchResults.map((result) => (
                  <Link
                    key={result.id}
                    to={`/ereg/folders`}
                    onClick={() => {
                      setSearchQuery('');
                      setSearchResults([]);
                    }}
                    className="flex items-center gap-3 px-4 py-3 transition-colors duration-150 hover:bg-gray-50 dark:hover:bg-gray-700/50 border-b border-gray-100 dark:border-gray-700 last:border-0"
                  >
                    <FileText className="w-5 h-5 text-gray-400 dark:text-gray-500" />
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-gray-900 dark:text-gray-100 truncate">
                        {result.document_name}
                      </p>
                      <p className="text-xs text-gray-500 dark:text-gray-400 truncate">
                        {result.folder?.path} • {result.document_type}
                      </p>
                    </div>
                  </Link>
                ))}
              </div>
            )}
          </div>
        }
      />

      {/* Stats Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
        {/* Pending Signatures */}
        <Link
          to="/ereg/signatures"
          className="bg-white rounded-xl border border-gray-200 p-5 hover:border-gray-300 hover:shadow-md transition-all duration-150 dark:bg-gray-800 dark:border-gray-700 dark:hover:border-gray-600"
        >
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-500 dark:text-gray-400">Pending Signatures</p>
              <p className="text-3xl font-bold text-gray-900 dark:text-gray-100 mt-1">
                {statsLoading ? '-' : stats?.pending_signatures ?? 0}
              </p>
            </div>
            <div className="p-2 rounded-lg bg-purple-100 dark:bg-purple-900/30">
              <PenTool className="text-purple-600 dark:text-purple-400" size={24} />
            </div>
          </div>
          <div className="mt-3 flex items-center text-sm text-purple-600 dark:text-purple-400 font-medium">
            Sign Now <ChevronRight size={16} />
          </div>
        </Link>

        {/* Missing Documents */}
        <div className="bg-white rounded-xl border border-gray-200 p-5 dark:bg-gray-800 dark:border-gray-700">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-500 dark:text-gray-400">Missing Documents</p>
              <p className="text-3xl font-bold text-gray-900 dark:text-gray-100 mt-1">
                {statsLoading ? '-' : stats?.missing_documents ?? 0}
              </p>
            </div>
            <div className="p-2 rounded-lg bg-amber-100 dark:bg-amber-900/30">
              <AlertTriangle className="text-amber-600 dark:text-amber-400" size={24} />
            </div>
          </div>
          <div className="mt-3 text-sm text-gray-500 dark:text-gray-400">
            Unfulfilled placeholders
          </div>
        </div>

        {/* Expiring Documents */}
        <div className="bg-white rounded-xl border border-gray-200 p-5 dark:bg-gray-800 dark:border-gray-700">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-500 dark:text-gray-400">Expiring Soon</p>
              <p className="text-3xl font-bold text-gray-900 dark:text-gray-100 mt-1">
                {statsLoading ? '-' : stats?.expiring_documents ?? 0}
              </p>
            </div>
            <div className="p-2 rounded-lg bg-red-100 dark:bg-red-900/30">
              <Clock className="text-red-600 dark:text-red-400" size={24} />
            </div>
          </div>
          <div className="mt-3 text-sm text-gray-500 dark:text-gray-400">
            Within 30 days
          </div>
        </div>

      </div>

      {/* Site Documents Quick Access */}
      {currentSite && (
        <div className="bg-white rounded-xl border border-gray-200 p-5 dark:bg-gray-800 dark:border-gray-700">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100 flex items-center gap-2">
              <FolderOpen size={20} className="text-primary-600 dark:text-primary-400" />
              Site Documents
            </h2>
            <Link
              to="/ereg/folders"
              onClick={handleSiteDocsClick}
              className="text-sm text-primary-600 hover:text-primary-700 dark:text-primary-400 dark:hover:text-primary-300 font-medium flex items-center gap-1"
            >
              Open <ChevronRight size={16} />
            </Link>
          </div>
          <p className="text-sm text-gray-600 dark:text-gray-400">
            Manage CVs, licenses, training certificates, and other site-level documents
            that can be linked to multiple studies.
          </p>
        </div>
      )}

      {/* Protocols List */}
      <div className="bg-white rounded-xl border border-gray-200 overflow-hidden dark:bg-gray-800 dark:border-gray-700">
        <div className="px-5 py-4 border-b border-gray-100 dark:border-gray-700">
          <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100 flex items-center gap-2">
            <FileText size={20} className="text-primary-600 dark:text-primary-400" />
            My Studies
          </h2>
        </div>

        {protocolsLoading ? (
          <div className="p-8 text-center">
            <div className="inline-block animate-spin rounded-full h-6 w-6 border-b-2 border-primary-600 dark:border-primary-400"></div>
            <p className="mt-2 text-sm text-gray-500 dark:text-gray-400">Loading studies...</p>
          </div>
        ) : protocols.length === 0 ? (
          <div className="p-8 text-center text-gray-500 dark:text-gray-400">
            <FileText size={40} className="mx-auto mb-2 text-gray-300 dark:text-gray-600" />
            <p>No studies available</p>
            <p className="text-sm mt-1">Create a new study to get started</p>
          </div>
        ) : (
          <div className="divide-y divide-gray-100 dark:divide-gray-700">
            {protocols.map((protocol) => {
              const protocolStat = stats?.protocols?.find(p => p.id === protocol.id);
              return (
                <Link
                  key={protocol.id}
                  to="/ereg/folders"
                  onClick={() => handleProtocolClick(protocol)}
                  className="flex items-center justify-between p-4 hover:bg-gray-50 dark:hover:bg-gray-700/50 transition-colors duration-150"
                >
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-3">
                      <h3 className="font-medium text-gray-900 dark:text-gray-100">
                        {protocol.protocol_number}
                      </h3>
                      <span className={`inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-medium ${
                        protocol.status === 'active'
                          ? 'bg-green-100 text-green-800 dark:bg-green-900/40 dark:text-green-300'
                          : protocol.status === 'startup'
                            ? 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/40 dark:text-yellow-300'
                            : protocol.status === 'closed'
                              ? 'bg-gray-100 text-gray-700 dark:bg-gray-700/60 dark:text-gray-300'
                              : 'bg-gray-100 text-gray-700 dark:bg-gray-700/60 dark:text-gray-300'
                      }`}>
                        {protocol.status}
                      </span>
                      {protocolStat && protocolStat.placeholder_total > 0 && (
                        <span className={`inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-medium ${
                          protocolStat.placeholder_fulfilled === protocolStat.placeholder_total
                            ? 'bg-green-100 text-green-800 dark:bg-green-900/40 dark:text-green-300'
                            : 'bg-amber-100 text-amber-800 dark:bg-amber-900/40 dark:text-amber-300'
                        }`}>
                          {protocolStat.placeholder_fulfilled}/{protocolStat.placeholder_total} docs
                        </span>
                      )}
                      {protocolStat && protocolStat.expiring_count > 0 && (
                        <span className="inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-medium bg-red-100 text-red-800 dark:bg-red-900/40 dark:text-red-300">
                          {protocolStat.expiring_count} expiring
                        </span>
                      )}
                    </div>
                    {protocol.protocol_title && (
                      <p className="text-sm text-gray-500 dark:text-gray-400 truncate mt-0.5">
                        {protocol.protocol_title}
                      </p>
                    )}
                    <div className="flex items-center gap-4 mt-1 text-xs text-gray-400 dark:text-gray-500">
                      {protocol.sponsor_name && (
                        <span>Sponsor: {protocol.sponsor_name}</span>
                      )}
                      {protocol.site?.name && (
                        <span>Site: {protocol.site.name}</span>
                      )}
                    </div>
                  </div>
                  <ChevronRight size={20} className="text-gray-400 flex-shrink-0" />
                </Link>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}
